import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ProfileScreen } from "../../screens/profile/ProfileScreen";
import { ScreenState } from "../../components/ScreenState";
import { TabBar } from "../../components/TabBar";
import { useCachedAsync } from "../../lib/useCachedAsync";
import { OfflineNote } from "../../components/OfflineNote";
import { downloadedBytes, formatSize } from "../../lib/offlinePacks";
import { fetchMe, deleteAccount, addExam } from "../../api/me";
import { ExamSwitcher } from "../../components/ExamSwitcher";
import { setActiveExam } from "../../lib/activeExam";
import { updateProfile } from "../../api/profile";
import { signOut, changePassword } from "../../api/auth";

/**
 * Profile, on the real account.
 *
 * Name, number, exam, entitlement and subjects were all hardcoded to "Tunde" —
 * so every student saw somebody else's profile.
 */
export function ProfileRoute() {
  const navigate = useNavigate();
  const { data, loading, error, reload, staleSince } = useCachedAsync("me", fetchMe);
  // What the downloads really take on this phone; it was hard-coded to 0.
  const [downloads, setDownloads] = useState(0);
  useEffect(() => {
    void downloadedBytes().then(setDownloads);
  }, []);

  const handleSaveName = async (firstName: string) => {
    await updateProfile({ firstName });
    reload();
  };

  // The new profile becomes the one shown, and its subjects are picked next —
  // the same picker onboarding uses, saving into that profile.
  const handleAddExam = async (exam: "JAMB" | "WAEC", examYear: number) => {
    await addExam(exam, examYear);
    setActiveExam(exam);
    navigate("/subjects", { state: { exam, adding: true } });
  };

  const handleChangePassword = async (current: string, newPassword: string) => {
    await changePassword(current, newPassword);
  };

  const handleLogout = async () => {
    await signOut();
    navigate("/");
  };

  const handleDeleteAccount = async () => {
    await deleteAccount();
    await signOut();
    navigate("/");
  };

  return (
    <ScreenState
      loading={loading}
      error={error}
      onRetry={reload}
      footer={<TabBar />}
      header={<ExamSwitcher exams={data?.exams} active={data?.activeExam} />}
    >
      <OfflineNote since={staleSince} />
      {data ? (
        <ProfileScreen
          firstName={data.student.firstName ?? null}
          email={data.student.email ?? ""}
          phone={data.student.phoneNumber ?? ""}
          exam={data.student.examType ?? ""}
          examYear={data.student.examYear ?? new Date().getFullYear() + 1}
          hasAllAccess={data.entitlement.hasFullAccess}
          subjects={data.subjects.map((subject) => subject.name)}
          downloadsSize={formatSize(downloads)}
          onSaveName={handleSaveName}
          onChangePassword={handleChangePassword}
          onLogout={() => void handleLogout()}
          onDeleteAccount={handleDeleteAccount}
          onDownloads={() => navigate("/downloads")}
          onChangeSubjects={() => navigate("/profile/subjects")}
          exams={data.exams}
          onAddExam={handleAddExam}
        />
      ) : null}
    </ScreenState>
  );
}
